// 7HAI-544: current legal-document versions + bundled PDF locations.
// Mirror of the backend legal.py constants — bump BOTH together (and the
// rendered content in src/pages/agreements/legalContent.tsx and the PDFs in
// public/legal/) when a new document version ships.
//
// 7HAI-968: gating is version-aware, and the two roles are split.
//
//   CURRENT_*  — what the app DISPLAYS, serves as a PDF, and stamps on a new
//                acceptance. Bump this the moment a new document ships.
//   REQUIRED_* — the MINIMUM stored version that satisfies the gate. Anything
//                below it (including a missing version) re-prompts.
//
// They are separate because bumping CURRENT_* is a content release and raising
// REQUIRED_* is a decision to interrupt every customer. Raising REQUIRED_* must
// land in the same release as the matching backend bump, or AFTER it — never
// before, and never quietly alongside a document update.
export const CURRENT_EULA_VERSION = "2026-08";
export const CURRENT_PRIVACY_VERSION = "2026-08";
// 7HAI-972: Bid's GDPR Article 28 Data Processing Agreement. Mirrors
// backend/legal.py CURRENT_DPA_VERSION — bump both together.
export const CURRENT_DPA_VERSION = "2026-08";

// All three ship as `null`, meaning "no floor yet — an acceptance on record is
// enough", which is exactly the behaviour before 7HAI-968. So this ships INERT:
// the client becomes CAPABLE of re-prompting without anyone being re-prompted.
// Same move the backend makes with ENFORCE_DPA_GATE — build the gate, ship it
// unarmed, arm it in the release that is ready for it.
//
// `null` rather than an old version string, because a version string CANNOT be
// inert here. A missing stored version is older than every version string (see
// utils/legalVersion — deliberately, so pre-versioning records re-prompt), and
// acceptances with no version exist on both sides:
//
//   - Privacy: acceptances predating 7HAI-544 carry `privacy_policy: 1` with no
//     `privacy_policy_version` at all.
//   - EULA: every deliberate writer stamps a version, but before 7HAI-943 a
//     generic company PATCH replaced the whole license JSONB instead of merging
//     it, dropping `version` while leaving `accepted` in place. That is fixed,
//     but any company clobbered before the fix is still accepted-without-a-
//     version today.
//
// Arming either of these therefore re-prompts those cohorts. That is CORRECT
// behaviour — an acceptance nobody can date is not evidence of accepting the
// current document — but it is a decision to interrupt customers, and it
// belongs to the release that raises the matching backend constant, not to the
// ticket that built the mechanism.
export const REQUIRED_EULA_VERSION: string | null = null;
export const REQUIRED_PRIVACY_VERSION: string | null = null;
// 7HAI-1049: the DPA floor IS armed, and it is the one document where arming is
// free. MIRROR OF backend/legal.py REQUIRED_DPA_VERSION — the two decide
// different things (the backend refuses the session, this decides whether the
// acceptance screen is offered) and they must not disagree, or a customer is
// refused everywhere with no screen that can clear it.
//
// Free because the Bid DPA has never been in production. No company has ever
// been shown one, so there is no acceptance for this floor to judge and the set
// it can interrupt today is empty. That window closes at the first acceptance:
// after it, raising this is a decision to interrupt real customers, and the
// unversioned rows in the database need a migration first. Doing it now costs
// nothing and means the NEXT correction can re-prompt; not doing it now means it
// cannot.
//
// Unlike the two above, which stay `null` — see the note on them. Nothing here
// is aliased to CURRENT_*: they are equal today only because this is the first
// version, and a typo fix must never re-prompt anybody.
export const REQUIRED_DPA_VERSION: string | null = "2026-08";

export const EULA_EFFECTIVE = "1 July 2026";
export const PRIVACY_EFFECTIVE = "1 July 2026";
export const DPA_EFFECTIVE = "16 August 2026";
// The same date in machine form, so the meta line can format it for the reader's
// locale — the CHROME around the agreement is still bilingual even though the
// agreement itself is English-only (7HAI-1049), and a Finnish page should not
// print an English date. DPA_EFFECTIVE above is the English rendering and the
// fallback if Intl is unavailable.
export const DPA_EFFECTIVE_ISO = "2026-08-16";

// 7HAI-1142: there are no bundled legal PDFs any more. EULA_PDF_URL and
// PRIVACY_PDF_URL pointed at files in public/legal/ — the last two survivors of
// the pattern 7HAI-1091 removed, where a stored PDF drifts from its source while
// every test stays green. All three documents are now rendered by the backend
// from the Markdown it serves as text, so the file a customer files and the words
// they read cannot be different generations of the agreement.

// 7HAI-1092: THE DPA HAS NO CONSTANT HERE ANY MORE, and that is the point.
//
// It used to be `/legal/dpa-2026-08-en.pdf`, a file bundled in public/. That file
// went stale the moment the source Markdown was corrected upstream under an
// unchanged version string, and nothing in this repository could notice — the
// source lives in pipe_time_factor. Its URL is now built at call time from the
// backend endpoint that renders it (see api/legalDocument.ts,
// legalDocumentPdfUrl), because a rendered document cannot drift from its source
// and a stored one can.
//
// The two above are still bundled: the backend serves no EULA or privacy policy
// for Bid, so there is nowhere yet to move them to.
